// AllMaintenanceReports.js - Professional Enterprise Reports Management

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { EditorState, convertFromRaw } from 'draft-js';
import Editor from '@draft-js-plugins/editor';
import createInlineToolbarPlugin from '@draft-js-plugins/inline-toolbar';
import 'draft-js/dist/Draft.css';
import axios from 'axios';
import { Host } from '../../App';
import { format, parseISO } from 'date-fns';
import real_l from "../../real_logo.png";
import './AllMaintenanceReports.css';

const inlineToolbarPlugin = createInlineToolbarPlugin();
const { InlineToolbar } = inlineToolbarPlugin;

const ShiftReport_All = () => {
  // Data States
  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Filter States
  const [filterDate, setFilterDate] = useState(new Date().toISOString().slice(0, 10));
  const [filterShift, setFilterShift] = useState('');
  const [filterSupervisor, setFilterSupervisor] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [activeQuickFilter, setActiveQuickFilter] = useState('all');

  // Date Range
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [Enddate, setEnddate] = useState(new Date().toISOString().slice(0, 10));

  // View & Sort
  const [viewMode, setViewMode] = useState('card'); // 'card' or 'list'
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [expandedReports, setExpandedReports] = useState([]);

  // Selection & Bulk Actions
  const [selectedReports, setSelectedReports] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);

  // Image Modal
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageIndex, setImageIndex] = useState(0);
  const [imageList, setImageList] = useState([]);

  // Export
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Statistics
  const [statistics, setStatistics] = useState({
    total: 0,
    morning: 0,
    evening: 0,
    today: 0,
    week: 0
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Refs
  const searchInputRef = useRef(null);
  const printRef = useRef(null);

  // User Info
  const stage = (window.localStorage.getItem("Stage") || '').replace(/[{""}]/g, "");
  const token = window.localStorage.getItem("acessToken");
  const User = JSON.parse(window.localStorage.getItem("Name"));
  const [rank, setRank] = useState(false);

  const nowDate = new Date();
  const formattedDate = nowDate.toLocaleDateString();
  const formattedTime = nowDate.toLocaleTimeString();

  // ===== EFFECTS =====
  useEffect(() => {
    if (stage <= 4) {
      setRank(true);
    }
  }, [stage]);

  useEffect(() => {
    fetchReports();

    // Keyboard shortcut for search
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [date, Enddate]);

  useEffect(() => {
    applyAllFilters();
  }, [reports, searchTerm, filterShift, filterSupervisor, activeQuickFilter, sortConfig]);

  // ===== API CALLS =====
  const fetchReports = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`https://${Host}/maintenance/get-reports`, {
        params: {
          token: token,
          date: date,
          EndDate: Enddate
        }
      });

      if (Array.isArray(response.data) && response.data.length > 0) {
        const fetchedReports = response.data
          .map((report) => {
            try {
              let parsedContent;
              let contentState;

              try {
                parsedContent = JSON.parse(report.content);
              } catch (error) {
                console.error('Error parsing JSON content:', error);
                parsedContent = null;
              }

              if (Array.isArray(parsedContent)) {
                contentState = parsedContent.map((section) => ({
                  heading: section.heading,
                  content: EditorState.createWithContent(
                    convertFromRaw(section.content)
                  ),
                }));
              } else if (parsedContent) {
                const rawContentState = convertFromRaw(parsedContent);
                contentState = [{ heading: 'Report Content', content: EditorState.createWithContent(rawContentState) }];
              }

              // Parse attachments
              let attachments = [];
              try {
                attachments = report.attachments ? JSON.parse(report.attachments) : [];
              } catch (e) {
                attachments = [];
              }

              return {
                ...report,
                contentState,
                attachments,
                hasAttachments: attachments.length > 0
              };
            } catch (error) {
              console.error('Error converting report content:', error);
              return null;
            }
          })
          .filter(Boolean);

        setReports(fetchedReports);
        setFilteredReports(fetchedReports);
        calculateStatistics(fetchedReports);
      } else {
        setReports([]);
        setFilteredReports([]);
        setStatistics({ total: 0, morning: 0, evening: 0, today: 0, week: 0 });
      }
    } catch (error) {
      console.error('Error fetching reports:', error);
      setReports([]);
      setFilteredReports([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ===== STATISTICS =====
  const calculateStatistics = (reportData) => {
    const today = new Date().toISOString().slice(0, 10);
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);

    setStatistics({
      total: reportData.length,
      morning: reportData.filter(r => r.shift === 'Morning').length,
      evening: reportData.filter(r => r.shift === 'Evening').length,
      today: reportData.filter(r => r.date?.slice(0, 10) === today).length,
      week: reportData.filter(r => r.date?.slice(0, 10) >= weekAgo).length
    });
  };

  // ===== FILTERING =====
  const applyAllFilters = useCallback(() => {
    let result = [...reports];

    // Quick Filters
    switch (activeQuickFilter) {
      case 'morning':
        result = result.filter(r => r.shift === 'Morning');
        break;
      case 'evening':
        result = result.filter(r => r.shift === 'Evening');
        break;
      case 'today':
        result = result.filter(r => r.date?.slice(0, 10) === new Date().toISOString().slice(0, 10));
        break;
      case 'week':
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
        result = result.filter(r => r.date?.slice(0, 10) >= weekAgo);
        break;
      case 'with_attachments':
        result = result.filter(r => r.hasAttachments);
        break;
      default:
        break;
    }

    // Shift Filter
    if (filterShift) {
      result = result.filter(r => r.shift === filterShift);
    }

    // Supervisor Filter
    if (filterSupervisor) {
      result = result.filter(r =>
        r.supervisor?.toLowerCase().includes(filterSupervisor.toLowerCase())
      );
    }

    // Search Term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(report =>
        report.supervisor?.toLowerCase().includes(term) ||
        report.shift?.toLowerCase().includes(term) ||
        report.date?.includes(term) ||
        (report.contentState && Array.isArray(report.contentState) &&
          report.contentState.some(section => {
            const contentText = section.content.getCurrentContent().getPlainText().toLowerCase();
            return contentText.includes(term) || section.heading?.toLowerCase().includes(term);
          })
        )
      );
    }

    // Sorting
    result.sort((a, b) => {
      const aVal = a[sortConfig.key] || '';
      const bVal = b[sortConfig.key] || '';

      if (sortConfig.key === 'created_at' || sortConfig.key === 'date') {
        return sortConfig.direction === 'asc'
          ? new Date(aVal) - new Date(bVal)
          : new Date(bVal) - new Date(aVal);
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredReports(result);
    setCurrentPage(1);
  }, [reports, searchTerm, filterShift, filterSupervisor, activeQuickFilter, sortConfig]);

  const handleQuickFilter = (filter) => {
    setActiveQuickFilter(activeQuickFilter === filter ? 'all' : filter);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterShift('');
    setFilterSupervisor('');
    setActiveQuickFilter('all');
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (activeQuickFilter !== 'all') count++;
    if (filterShift) count++;
    if (filterSupervisor) count++;
    if (searchTerm) count++;
    return count;
  };

  // ===== REPORT ACTIONS =====
  const toggleReportExpand = (reportId) => {
    setExpandedReports(prev =>
      prev.includes(reportId)
        ? prev.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const handleSelectReport = (reportId) => {
    setSelectedReports(prev =>
      prev.includes(reportId)
        ? prev.filter(id => id !== reportId)
        : [...prev, reportId]
    );
  };

  const handleSelectAll = () => {
    if (selectedReports.length === currentItems.length) {
      setSelectedReports([]);
    } else {
      setSelectedReports(currentItems.map(r => r.id));
    }
  };

  // ===== PRINT =====
  const handlePrint = (reportId) => {
    const selectedReport = document.querySelector(`#report-${reportId}`);
    const printWindow = window.open("", "", "height=600,width=800");

    const img = document.createElement('img');
    img.src = real_l;

    printWindow.document.write(`
      <html>
        <head>
          <title>Maintenance Report</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
            
            * { margin: 0; padding: 0; box-sizing: border-box; }
            
            body {
              font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
              padding: 40px;
              color: #1e293b;
              line-height: 1.6;
            }
            
            .print-header {
              text-align: center;
              border-bottom: 3px solid #4f46e5;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            
            .print-header img {
              height: 60px;
              margin-bottom: 10px;
            }
            
            .print-header h1 {
              font-size: 24px;
              color: #4f46e5;
              margin: 10px 0;
            }
            
            .print-meta {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
              font-size: 14px;
              color: #64748b;
              padding: 10px 0;
              border-bottom: 1px solid #e2e8f0;
            }
            
            .report-content {
              margin-top: 20px;
            }
            
            .report-content h2 {
              font-size: 18px;
              color: #0f172a;
              margin: 15px 0 10px;
              padding-bottom: 5px;
              border-bottom: 2px solid #e2e8f0;
            }
            
            .report-content p {
              margin-bottom: 10px;
              font-size: 14px;
            }
            
            .print-footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #e2e8f0;
              font-size: 12px;
              color: #94a3b8;
              text-align: right;
            }
            
            button, #attachment, .no-print {
              display: none !important;
            }
            
            img, video {
              max-width: 100%;
              border-radius: 8px;
              margin: 10px 0;
            }
            
            @media print {
              body { padding: 20px; }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            ${img.outerHTML}
            <h1>Maintenance Report</h1>
          </div>
          <div class="print-meta">
            <span>Printed By: ${User}</span>
            <span>Date: ${formattedDate} ${formattedTime}</span>
          </div>
          <div class="report-content">
            ${selectedReport?.innerHTML || ''}
          </div>
          <div class="print-footer">
            <p>This is an official maintenance report. For any queries, please contact the maintenance department.</p>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const handleBulkPrint = () => {
    if (selectedReports.length === 0) {
      alert('Please select at least one report to print');
      return;
    }
    selectedReports.forEach(id => handlePrint(id));
  };

  // ===== IMAGE MODAL =====
  const openImageModal = (images, index) => {
    setImageList(images);
    setImageIndex(index);
    setSelectedImage(images[index]);
  };

  const closeImageModal = () => {
    setSelectedImage(null);
    setImageList([]);
    setImageIndex(0);
  };

  const navigateImage = (direction) => {
    const newIndex = imageIndex + direction;
    if (newIndex >= 0 && newIndex < imageList.length) {
      setImageIndex(newIndex);
      setSelectedImage(imageList[newIndex]);
    }
  };

  // ===== EXPORT =====
  const handleExport = async (format) => {
    setExportLoading(true);
    try {
      const response = await axios.get(`https://${Host}/maintenance/reports/export`, {
        params: {
          token,
          format,
          date,
          EndDate: Enddate,
          ids: selectedReports.length > 0 ? selectedReports.join(',') : undefined
        },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `maintenance-reports-${date}-to-${Enddate}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting reports:', error);
      alert('Failed to export reports');
    } finally {
      setExportLoading(false);
    }
  };

  // ===== PAGINATION =====
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredReports.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredReports.length / itemsPerPage);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  // ===== HELPERS =====
  const getUniqueSupervisors = () => {
    return [...new Set(reports.map(r => r.supervisor).filter(Boolean))];
  };

  const formatReportDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'PPpp');
    } catch {
      return dateString;
    }
  };

  const getShiftColor = (shift) => {
    return shift === 'Morning' ?
      { bg: '#fef3c7', text: '#92400e', icon: '🌅' } :
      { bg: '#dbeafe', text: '#1e40af', icon: '🌙' };
  };

  // ===== RENDER =====
  return (
    <div className="amr-container">
      {/* Page Header */}
      {/* Search & Filters Section */}
      <div className="amr-search-section">
        <div className="amr-search-bar">
          <i className="fas fa-search amr-search-icon"></i>
          <input
            ref={searchInputRef}
            type="text"
            className="amr-search-input"
            placeholder="Search reports by supervisor, shift, content, date..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button
              className="amr-search-clear"
              onClick={() => setSearchTerm('')}
            >
              <i className="fas fa-times"></i>
            </button>
          )}
          <div className="amr-search-shortcut">
            <kbd>Ctrl</kbd> + <kbd>K</kbd>
          </div>
        </div>

        {/* Quick Filters */}
        <div className="amr-quick-filters">
          <button
            className={`amr-quick-filter-btn ${activeQuickFilter === 'all' ? 'active' : ''}`}
            onClick={() => handleQuickFilter('all')}
          >
            All Reports
          </button>
          <button
            className={`amr-quick-filter-btn ${activeQuickFilter === 'morning' ? 'active' : ''}`}
            onClick={() => handleQuickFilter('morning')}
          >
            🌅 Morning
          </button>
          <button
            className={`amr-quick-filter-btn ${activeQuickFilter === 'evening' ? 'active' : ''}`}
            onClick={() => handleQuickFilter('evening')}
          >
            🌙 Evening
          </button>
          <button
            className={`amr-quick-filter-btn ${activeQuickFilter === 'today' ? 'active' : ''}`}
            onClick={() => handleQuickFilter('today')}
          >
            Today
          </button>
          <button
            className={`amr-quick-filter-btn ${activeQuickFilter === 'week' ? 'active' : ''}`}
            onClick={() => handleQuickFilter('week')}
          >
            This Week
          </button>
          <button
            className={`amr-quick-filter-btn ${activeQuickFilter === 'with_attachments' ? 'active' : ''}`}
            onClick={() => handleQuickFilter('with_attachments')}
          >
            📎 With Attachments
          </button>
        </div>

        {/* Filter Actions */}
        {/* Filter Actions - Replace the existing amr-filter-actions div */}
        <div className="amr-filter-actions">
          <div className="amr-filter-actions-left">
            <button
              className="amr-btn amr-btn-outline amr-btn-sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            >
              <i className={`fas fa-${showAdvancedFilters ? 'times' : 'filter'}`}></i>
              {showAdvancedFilters ? 'Hide Filters' : 'Advanced Filters'}
              {getActiveFilterCount() > 0 && (
                <span className="amr-filter-count">{getActiveFilterCount()}</span>
              )}
            </button>
            {getActiveFilterCount() > 0 && (
              <button
                className="amr-btn amr-btn-ghost amr-btn-sm"
                onClick={clearAllFilters}
              >
                Clear All Filters
              </button>
            )}
          </div>
          <div className="amr-filter-actions-right">
            <div className="amr-items-per-page">
              <span>Show</span>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="amr-select-sm"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>reports</span>
            </div>
          </div>
        </div>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div className="amr-advanced-filters">
          <div className="amr-filter-grid">
            <div className="amr-filter-group">
              <label>Date Range</label>
              <div className="amr-date-range">
                <div className="amr-date-field">
                  <input
                    type="date"
                    className="amr-form-input"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </div>
                <div className="amr-date-field">
                  <input
                    type="date"
                    className="amr-form-input"
                    value={Enddate}
                    onChange={(e) => setEnddate(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="amr-filter-group">
              <label>Shift</label>
              <select
                className="amr-form-input"
                value={filterShift}
                onChange={(e) => setFilterShift(e.target.value)}
              >
                <option value="">All Shifts</option>
                <option value="Morning">Morning</option>
                <option value="Evening">Evening</option>
              </select>
            </div>
            <div className="amr-filter-group">
              <label>Supervisor</label>
              <select
                className="amr-form-input"
                value={filterSupervisor}
                onChange={(e) => setFilterSupervisor(e.target.value)}
              >
                <option value="">All Supervisors</option>
                {getUniqueSupervisors().map((supervisor) => (
                  <option key={supervisor} value={supervisor}>
                    {supervisor}
                  </option>
                ))}
              </select>
            </div>
            <div className="amr-filter-group">
              <label>Sort By</label>
              <select
                className="amr-form-input"
                value={sortConfig.key}
                onChange={(e) => handleSort(e.target.value)}
              >
                <option value="created_at">Date Created</option>
                <option value="date">Report Date</option>
                <option value="shift">Shift</option>
                <option value="supervisor">Supervisor</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Actions Bar */}
      {selectedReports.length > 0 && (
        <div className="amr-bulk-actions-bar">
          <div className="amr-bulk-info">
            <i className="fas fa-check-square"></i>
            <span>{selectedReports.length} report{selectedReports.length > 1 ? 's' : ''} selected</span>
          </div>
          <div className="amr-bulk-actions">
            <button
              className="amr-btn amr-btn-primary amr-btn-sm"
              onClick={handleBulkPrint}
            >
              <i className="fas fa-print"></i> Print Selected
            </button>
            <button
              className="amr-btn amr-btn-outline amr-btn-sm"
              onClick={() => setShowExportModal(true)}
            >
              <i className="fas fa-download"></i> Export Selected
            </button>
            <button
              className="amr-btn amr-btn-ghost amr-btn-sm"
              onClick={() => setSelectedReports([])}
            >
              Clear Selection
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="amr-content-area">
        {isLoading ? (
          <div className="amr-loading-state">
            <div className="amr-loading-spinner">
              <div className="amr-spinner-ring"></div>
              <p>Loading reports...</p>
            </div>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="amr-empty-state">
            <div className="amr-empty-icon">
              <i className="fas fa-file-alt"></i>
            </div>
            <h3>No reports found</h3>
            <p>
              {getActiveFilterCount() > 0
                ? 'Try adjusting your filters or date range'
                : 'No maintenance reports found for the selected date range'}
            </p>
            {getActiveFilterCount() > 0 && (
              <button className="amr-btn amr-btn-outline" onClick={clearAllFilters}>
                Clear All Filters
              </button>
            )}
          </div>
        ) : viewMode === 'card' ? (
          /* Card View */
          <div className="amr-reports-grid">
            {currentItems.map((report) => {
              const shiftColor = getShiftColor(report.shift);
              const isExpanded = expandedReports.includes(report.id);
              const isSelected = selectedReports.includes(report.id);

              return (
                <div
                  key={report.id}
                  className={`amr-report-card ${isSelected ? 'selected' : ''} ${isExpanded ? 'expanded' : ''}`}
                  id={`report-${report.id}`}
                >
                  {/* Card Header */}
                  <div className="amr-card-header">
                    <div className="amr-card-header-left">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleSelectReport(report.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="amr-card-checkbox"
                      />
                      <div className="amr-card-date-badge">
                        {report.date ? report.date.slice(0, 10) : 'N/A'}
                      </div>
                    </div>
                    <div className="amr-card-header-right">
                      <span
                        className="amr-shift-badge"
                        style={{ background: shiftColor.bg, color: shiftColor.text }}
                      >
                        {shiftColor.icon} {report.shift}
                      </span>
                    </div>
                  </div>

                  {/* Card Meta */}
                  <div className="amr-card-meta">
                    <div className="amr-card-supervisor">
                      <div className="amr-supervisor-avatar">
                        {report.supervisor?.charAt(0)?.toUpperCase() || 'S'}
                      </div>
                      <div className="amr-supervisor-info">
                        <span className="amr-supervisor-name">{report.supervisor || 'Unknown'}</span>
                        <span className="amr-supervisor-label">Supervisor</span>
                      </div>
                    </div>
                    <div className="amr-card-time">
                      <i className="far fa-clock"></i>
                      {formatReportDate(report.created_at)}
                    </div>
                  </div>

                  {/* Card Content Preview */}
                  <div className="amr-card-content-preview" onClick={() => toggleReportExpand(report.id)}>
                    {report.contentState && Array.isArray(report.contentState) ? (
                      report.contentState.slice(0, isExpanded ? undefined : 1).map((section, index) => (
                        <div key={index} className="amr-content-section">
                          <h4 className="amr-section-heading">{section.heading}</h4>
                          <div className="amr-section-text">
                            <Editor
                              editorState={section.content}
                              readOnly
                              plugins={[inlineToolbarPlugin]}
                            />
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="amr-no-content">No content available</p>
                    )}

                    {!isExpanded && report.contentState && report.contentState.length > 1 && (
                      <div className="amr-show-more">
                        <span>+ {report.contentState.length - 1} more section{report.contentState.length - 1 > 1 ? 's' : ''}</span>
                        <i className="fas fa-chevron-down"></i>
                      </div>
                    )}
                  </div>

                  {/* Attachments */}
                  {report.hasAttachments && (
                    <div className="amr-card-attachments">
                      <div className="amr-attachments-label">
                        <i className="fas fa-paperclip"></i>
                        <span>Attachments ({report.attachments.length})</span>
                      </div>
                      <div className="amr-attachments-grid">
                        {report.attachments.map((attachment, index) => (
                          <div
                            key={index}
                            className="amr-attachment-thumb"
                            onClick={() => openImageModal(report.attachments, index)}
                          >
                            {attachment.includes('.mp4') ? (
                              <div className="amr-video-thumb">
                                <i className="fas fa-play"></i>
                                <span>Video</span>
                              </div>
                            ) : (
                              <img
                                src={attachment}
                                alt={`Attachment ${index + 1}`}
                                loading="lazy"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Card Actions */}
                  <div className="amr-card-actions">
                    <button
                      className="amr-btn amr-btn-sm amr-btn-outline"
                      onClick={() => toggleReportExpand(report.id)}
                    >
                      <i className={`fas fa-${isExpanded ? 'compress' : 'expand'}`}></i>
                      {isExpanded ? 'Collapse' : 'Expand'}
                    </button>
                    <button
                      className="amr-btn amr-btn-sm amr-btn-primary"
                      onClick={() => handlePrint(report.id)}
                    >
                      <i className="fas fa-print"></i>
                      Print
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* List View */
          <div className="amr-list-view">
            <div className="amr-list-header">
              <div className="amr-list-checkbox">
                <input
                  type="checkbox"
                  checked={selectedReports.length === currentItems.length && currentItems.length > 0}
                  onChange={handleSelectAll}
                />
              </div>
              <div className="amr-list-date" onClick={() => handleSort('date')}>
                Date {sortConfig.key === 'date' && <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>}
              </div>
              <div className="amr-list-shift" onClick={() => handleSort('shift')}>
                Shift {sortConfig.key === 'shift' && <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>}
              </div>
              <div className="amr-list-supervisor" onClick={() => handleSort('supervisor')}>
                Supervisor {sortConfig.key === 'supervisor' && <i className={`fas fa-sort-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>}
              </div>
              <div className="amr-list-preview">Content Preview</div>
              <div className="amr-list-actions">Actions</div>
            </div>
            {currentItems.map((report) => {
              const shiftColor = getShiftColor(report.shift);
              const isSelected = selectedReports.includes(report.id);

              return (
                <div key={report.id} className={`amr-list-item ${isSelected ? 'selected' : ''}`}>
                  <div className="amr-list-checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelectReport(report.id)}
                    />
                  </div>
                  <div className="amr-list-date">
                    {report.date ? report.date.slice(0, 10) : 'N/A'}
                  </div>
                  <div className="amr-list-shift">
                    <span
                      className="amr-shift-badge"
                      style={{ background: shiftColor.bg, color: shiftColor.text }}
                    >
                      {shiftColor.icon} {report.shift}
                    </span>
                  </div>
                  <div className="amr-list-supervisor">
                    {report.supervisor || 'Unknown'}
                  </div>
                  <div className="amr-list-preview">
                    {report.contentState && Array.isArray(report.contentState) && report.contentState[0] ? (
                      <span className="amr-preview-text">
                        {report.contentState[0].content.getCurrentContent().getPlainText().substring(0, 100)}...
                      </span>
                    ) : 'No content'}
                  </div>
                  <div className="amr-list-actions">
                    <button
                      className="amr-action-btn amr-btn-view"
                      onClick={() => handlePrint(report.id)}
                      title="Print Report"
                    >
                      <i className="fas fa-print"></i>
                    </button>
                    <button
                      className="amr-action-btn amr-btn-expand"
                      onClick={() => toggleReportExpand(report.id)}
                      title="Expand Report"
                    >
                      <i className={`fas fa-${expandedReports.includes(report.id) ? 'compress' : 'expand'}`}></i>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredReports.length > itemsPerPage && (
        <div className="amr-pagination-container">
          <div className="amr-pagination-info">
            Showing <strong>{indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredReports.length)}</strong> of <strong>{filteredReports.length}</strong> reports
          </div>
          <div className="amr-pagination-controls">
            <button
              className="amr-page-btn"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              <i className="fas fa-angle-double-left"></i>
            </button>
            <button
              className="amr-page-btn"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              <i className="fas fa-angle-left"></i>
            </button>

            {getPageNumbers().map(page => (
              <button
                key={page}
                className={`amr-page-btn ${currentPage === page ? 'active' : ''}`}
                onClick={() => setCurrentPage(page)}
              >
                {page}
              </button>
            ))}

            <button
              className="amr-page-btn"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              <i className="fas fa-angle-right"></i>
            </button>
            <button
              className="amr-page-btn"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              <i className="fas fa-angle-double-right"></i>
            </button>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {selectedImage && (
        <div className="amr-modal-overlay" onClick={closeImageModal}>
          <div className="amr-modal amr-image-modal" onClick={(e) => e.stopPropagation()}>
            <div className="amr-modal-header">
              <h3>Attachment {imageIndex + 1} of {imageList.length}</h3>
              <div className="amr-modal-header-actions">
                <a
                  href={selectedImage}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="amr-btn amr-btn-sm amr-btn-outline"
                >
                  <i className="fas fa-download"></i>
                </a>
                <button className="amr-modal-close" onClick={closeImageModal}>×</button>
              </div>
            </div>
            <div className="amr-modal-body amr-image-modal-body">
              {imageList.length > 1 && (
                <>
                  <button
                    className="amr-image-nav amr-image-prev"
                    onClick={() => navigateImage(-1)}
                    disabled={imageIndex === 0}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  <button
                    className="amr-image-nav amr-image-next"
                    onClick={() => navigateImage(1)}
                    disabled={imageIndex === imageList.length - 1}
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </>
              )}
              {selectedImage.includes('.mp4') ? (
                <video controls src={selectedImage} style={{ maxWidth: '100%', maxHeight: '70vh' }}>
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={selectedImage}
                  alt="Attachment Full Size"
                  style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="amr-modal-overlay" onClick={() => setShowExportModal(false)}>
          <div className="amr-modal amr-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="amr-modal-header">
              <h3>Export Reports</h3>
              <button className="amr-modal-close" onClick={() => setShowExportModal(false)}>×</button>
            </div>
            <div className="amr-modal-body">
              <p>Choose export format{selectedReports.length > 0 ? ` for ${selectedReports.length} selected reports` : ''}:</p>
              <div className="amr-export-options">
                <button
                  className="amr-export-option"
                  onClick={() => handleExport('csv')}
                  disabled={exportLoading}
                >
                  <i className="fas fa-file-csv"></i>
                  <span>CSV</span>
                </button>
                <button
                  className="amr-export-option"
                  onClick={() => handleExport('xlsx')}
                  disabled={exportLoading}
                >
                  <i className="fas fa-file-excel"></i>
                  <span>Excel</span>
                </button>
                <button
                  className="amr-export-option"
                  onClick={() => handleExport('pdf')}
                  disabled={exportLoading}
                >
                  <i className="fas fa-file-pdf"></i>
                  <span>PDF</span>
                </button>
              </div>
              {exportLoading && <p className="amr-export-loading">Preparing export...</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftReport_All;